import React from 'react';
import { Box, Container, Typography, Grid, Button } from '@mui/material';
import { Link as NavLink } from "react-scroll";
import CodeIcon from '@mui/icons-material/Code';
import WebIcon from '@mui/icons-material/Web';
import CloudIcon from '@mui/icons-material/Cloud';

// Helper function to get AppBar height
const getAppBarHeight = () => 64;

export default function SoftwareSolutionsSection() {
  const appBarHeight = getAppBarHeight();

  const softwareExamples = [
    {
      icon: <WebIcon sx={{ fontSize: '3rem', color: '#2561b3' }} />,
      title: "Interactive Websites & Web Applications",
      description: "From elegant brochure sites to complex e-commerce platforms, we create responsive, accessible, and user-focused web experiences that drive engagement and conversions."
    },
    {
      icon: <CloudIcon sx={{ fontSize: '3rem', color: '#2561b3' }} />,
      title: "Data Infrastructure & Cloud Solutions", 
      description: "Build robust, scalable data pipelines and cloud-native applications that handle your growing data needs with reliability, security, and optimal performance."
    },
    {
      icon: <CodeIcon sx={{ fontSize: '3rem', color: '#2561b3' }} />,
      title: "Custom Business Applications",
      description: "Bespoke software solutions tailored to your unique business processes, from internal tools to customer-facing applications that streamline operations."
    }
  ];

  return (
    <Box id="software-solutions-section" className="snap-section" sx={{ 
      position: 'relative',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
      paddingTop: `${appBarHeight}px`,
      paddingY: 10,
      overflow: 'hidden'
    }}>
      {/* Subtle background pattern */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.02,
        backgroundImage: `radial-gradient(circle at 25px 25px, #2561b3 2px, transparent 0), radial-gradient(circle at 75px 75px, #2561b3 2px, transparent 0)`,
        backgroundSize: '100px 100px',
        backgroundPosition: '0 0, 50px 50px'
      }} />
      
      {/* Floating background elements */}
      <Box sx={{
        position: 'absolute',
        top: '15%',
        right: '5%',
        width: 200,
        height: 200,
        background: 'linear-gradient(135deg, rgba(37, 97, 179, 0.1), rgba(96, 165, 250, 0.05))',
        borderRadius: '50%',
        animation: 'float 8s ease-in-out infinite',
        filter: 'blur(30px)'
      }} />
      
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', marginBottom: 8 }}>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              fontWeight: 800,
              background: 'linear-gradient(135deg, #2561b3 0%, #1e40af 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              marginBottom: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Software Solutions
          </Typography>
          <Box sx={{ 
            width: 80, 
            height: 4, 
            background: 'linear-gradient(90deg, #2561b3, #60a5fa)',
            margin: '0 auto 3rem auto',
            borderRadius: 2
          }} />
          
          {/* Description Blob */}
          <Box sx={{
            maxWidth: '800px',
            marginX: 'auto',
            marginBottom: 8,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(248,250,252,0.9) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            padding: 4,
            border: '1px solid rgba(37, 97, 179, 0.1)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.08)'
          }}>
            <Typography variant="h5" component="p" sx={{ 
              color: '#1e293b',
              lineHeight: 1.7,
              fontWeight: 400,
              fontSize: { xs: '1.2rem', md: '1.3rem' }
            }}>
              Datasky delivers <strong>bespoke software development</strong> solutions that transform your vision into digital reality. Using modern technologies and industry best practices, we create <strong>reliable, secure, accessible, and responsive</strong> software that drives business growth and enhances user experiences.
            </Typography>
          </Box>
        </Box>

        {/* Examples Grid */}
        <Grid container spacing={4} sx={{ marginBottom: 8 }}>
          {softwareExamples.map((example, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box sx={{
                height: '100%',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                borderRadius: 3,
                padding: 4,
                border: '1px solid rgba(226, 232, 240, 0.8)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 20px 40px rgba(37, 97, 179, 0.15)',
                  '& .example-icon': {
                    transform: 'scale(1.1) rotate(5deg)'
                  }
                }
              }}>
                {/* Decorative corner */}
                <Box sx={{
                  position: 'absolute',
                  top: -10,
                  right: -10,
                  width: 60,
                  height: 60,
                  background: 'linear-gradient(135deg, #2561b3, #60a5fa)',
                  borderRadius: '50%',
                  opacity: 0.1
                }} />
                
                <Box 
                  className="example-icon"
                  sx={{ 
                    marginBottom: 3,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 2
                  }}
                >
                  {example.icon}
                </Box>
                
                <Typography variant="h5" component="h3" sx={{ 
                  fontWeight: 700, 
                  marginBottom: 2,
                  color: '#1e293b',
                  fontSize: '1.4rem'
                }}>
                  {example.title}
                </Typography>
                
                <Typography variant="body1" sx={{ 
                  color: '#64748b', 
                  lineHeight: 1.7,
                  fontSize: '1rem'
                }}>
                  {example.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Box sx={{ 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          borderRadius: 4,
          padding: { xs: 6, md: 8 },
          marginTop: 16,
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          {/* CTA Background effect */}
          <Box sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 150,
            height: 150,
            background: 'linear-gradient(135deg, rgba(37, 97, 179, 0.3), rgba(96, 165, 250, 0.2))',
            borderRadius: '50%',
            animation: 'float 6s ease-in-out infinite'
          }} />
          
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Typography variant="h3" component="h4" sx={{ 
              marginBottom: 2, 
              fontWeight: 700,
              color: 'white',
              fontSize: { xs: '1.8rem', md: '2.2rem' }
            }}>
              Ready to Build Your Next Software Solution?
            </Typography>
            
            <Typography variant="h6" component="p" sx={{ 
              marginBottom: 4,
              color: 'rgba(255, 255, 255, 0.8)',
              fontWeight: 400,
              maxWidth: '600px',
              marginX: 'auto'
            }}>
              Let's discuss your project requirements and create software that exceeds expectations
            </Typography>
            
            <NavLink 
              to="contact-section" 
              spy
              smooth
              offset={-appBarHeight}
              duration={500}
              style={{ textDecoration: 'none' }}
            >
              <Button 
                variant="contained" 
                size="large" 
                sx={{
                  background: 'linear-gradient(135deg, #2561b3 0%, #3b82f6 100%)',
                  color: 'white',
                  fontWeight: 700,
                  paddingX: 5,
                  paddingY: 2,
                  fontSize: '1.1rem',
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: '0 8px 25px rgba(37, 97, 179, 0.4)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 35px rgba(37, 97, 179, 0.5)'
                  }
                }}
              >
                Start Your Software Project
              </Button>
            </NavLink>
          </Box>
        </Box>
      </Container>
      
      {/* Add keyframes for floating animation */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </Box>
  );
} 